// src/components/agent/ChatSidebar.js
//
// The persistent history rail (frontend spec §8.5) — replaces the ChatHistory
// modal. One live listener (useChatList), day-bucket groups, active-turn
// dots, kebab rename/delete (delete-confirm carried from ChatHistory, and
// disabled while that chat's turn runs). Collapses to a 56px icon rail.
import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Drawer, List, ListItemButton, ListItemText, Typography, IconButton, CircularProgress,
  Tooltip, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, ListItemIcon, Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { doc, runTransaction } from 'firebase/firestore';
import LoadFailure from '../common/LoadFailure';
import { db } from '../../services/firebase';
import RenameDialog from '../assistant/RenameDialog';
import LeftSidebar from '../common/LeftSidebar';
import SidebarFooter from '../common/SidebarFooter';

const ActiveTurnDot = styled('span')(({ theme }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: theme.palette.success.main,
  flexShrink: 0,
  animation: 'turnDotPulse 1.6s ease-in-out infinite',
  '@keyframes turnDotPulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.4 },
  },
}));

function SidebarContent({
  companyName, chats, groups, loading, error, onRetry, activeChatId, activeChatName, onSelectChat, onNewChat,
  onDeleteChat, hasOlder, loadingOlder, onLoadOlder, onRenamed, onDeleted,
}) {
  const [menuState, setMenuState] = useState(null); // { anchorEl, chat }
  const listRef = useRef(null);
  const sentinelRef = useRef(null);
  // The last row of the list asks for the next page of older chats when it
  // comes into view: a list that does not fill the sidebar loads until it does,
  // and a longer one loads when it is scrolled to its end.
  //
  // The row used to be chosen by counting fifty back from the end, so that the
  // next page was ready before the end was reached. Such a row can be on screen
  // with the list unscrolled: always for a list of fifty rows or fewer, where
  // the count stopped at the first row, and for a list a screenful longer than
  // that. A page that adds no row then asks for another at once, and pages do
  // add no row, because chats of other platforms are filtered out after the
  // read. That paged through the whole collection (bug 15.2).
  const lastChat = chats.length > 0 ? chats[chats.length - 1] : null;
  const sentinelId = hasOlder && !loadingOlder && lastChat ? lastChat.id : null;
  useEffect(() => {
    const row = sentinelRef.current;
    const root = listRef.current;
    if (!sentinelId || !row || !root) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) onLoadOlder();
    }, { root });
    observer.observe(row);
    return () => observer.disconnect();
  }, [sentinelId, onLoadOlder]);
  const [renameTarget, setRenameTarget] = useState(null); // chat row
  const [deleteTarget, setDeleteTarget] = useState(null); // chat row

  const closeMenu = () => setMenuState(null);

  const handleRename = async (newName) => {
    if (!renameTarget || !companyName) return;
    try {
      const chatRef = doc(db, 'users', companyName, 'agents', renameTarget.id);
      await runTransaction(db, async (transaction) => {
        const snapshot = await transaction.get(chatRef);
        if (!snapshot.exists()) throw new Error('This chat no longer exists.');
        if (snapshot.data().deleting) throw new Error('This chat is being deleted.');
        transaction.update(chatRef, { chat_name: newName });
      });
      onRenamed?.(renameTarget.id, newName);
    } catch (error) {
      // The rename dialog awaits this and shows the failure inline.
      console.error('Error renaming chat:', error);
      throw error;
    }
  };

  // The page owns the delete request, the move away from an open chat and the
  // outcome message. The row leaves the list only once the backend has
  // confirmed the delete: a refused or failed delete keeps it (B9-8).
  const handleDelete = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    if (!target) return;
    try {
      await onDeleteChat(target.id);
    } catch (error) {
      return; // Nothing was deleted; the page has already said why.
    }
    onDeleted?.(target.id);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, flexShrink: 0 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={onNewChat}
          sx={{ flex: 1, textTransform: 'none', justifyContent: 'flex-start' }}
        >
          New chat
        </Button>
      </Box>

      <Box ref={listRef} sx={{ flex: 1, overflowY: 'auto', minHeight: 0, px: 0.5, pb: 1 }}>
        {error && <LoadFailure thing="your chats" onRetry={onRetry} sx={{ px: 1.5 }} />}
        {!loading && !error && groups.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 2, textAlign: 'center' }}>
            No chats yet — your conversations will appear here
          </Typography>
        )}
        {groups.map((group) => (
          <Box key={group.label} sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="text.disabled" sx={{ px: 1.5, pt: 1, display: 'block', fontWeight: 600 }}>
              {group.label}
            </Typography>
            <List dense disablePadding>
              {group.chats.map((chat) => (
                <ListItemButton
                  key={chat.id}
                  ref={chat.id === sentinelId ? sentinelRef : undefined}
                  selected={chat.id === activeChatId}
                  onClick={() => { if (!chat.deleting) onSelectChat(chat.id); }}
                  sx={{
                    borderRadius: 1,
                    mx: 0.5,
                    pr: 0.5,
                    '&.Mui-selected': { backgroundColor: 'background.alt' },
                  }}
                >
                  {chat.hasLiveTurn && (
                    <Tooltip title="Agent is working">
                      <ActiveTurnDot style={{ marginRight: 8 }} />
                    </Tooltip>
                  )}
                  <ListItemText
                    primary={chat.id === activeChatId && activeChatName ? activeChatName : chat.chatName}
                    secondary={chat.deleting
                      ? (chat.deletionInProgress ? 'Deleting…' : 'Deletion interrupted — retry from the menu')
                      : null}
                    primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuState({ anchorEl: e.currentTarget, chat });
                    }}
                    sx={{ opacity: 0.7 }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </ListItemButton>
              ))}
            </List>
          </Box>
        ))}
        {loadingOlder && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={16} />
          </Box>
        )}
      </Box>

      <Box sx={{ px: 1.5, pb: 1.5, flexShrink: 0 }}>
        <SidebarFooter page="agent" />
      </Box>

      <Menu
        anchorEl={menuState?.anchorEl}
        open={!!menuState}
        onClose={closeMenu}
      >
        <MenuItem
          disabled={Boolean(menuState?.chat?.deleting)}
          onClick={() => {
            setRenameTarget(menuState.chat);
            closeMenu();
          }}
        >
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          Rename
        </MenuItem>
        <Divider />
        <Tooltip title={menuState?.chat?.hasLiveTurn ? 'The agent is working on this chat' : ''}>
          <span>
            <MenuItem
              disabled={!!menuState?.chat?.hasLiveTurn || !!menuState?.chat?.deletionInProgress}
              onClick={() => {
                setDeleteTarget(menuState.chat);
                closeMenu();
              }}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
              {menuState?.chat?.deleting ? 'Retry deletion' : 'Delete'}
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>

      {renameTarget && (
        <RenameDialog
          open
          onClose={() => setRenameTarget(null)}
          onSave={handleRename}
          initialName={renameTarget.chatName || ''}
        />
      )}

      {/* Delete confirmation — carried from ChatHistory. */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{deleteTarget?.deleting ? 'Retry chat deletion' : 'Delete Chat'}</DialogTitle>
        <DialogContent>
          <Typography>{deleteTarget?.deleting
            ? 'Retry deleting the remaining chat data?'
            : 'Are you sure you want to delete this chat?'}</Typography>
          {/* The row's name, live for the open chat exactly as the list shows it. */}
          <Typography sx={{ fontWeight: 600, mt: 1.5, wordBreak: 'break-word' }}>
            {deleteTarget && (deleteTarget.id === activeChatId && activeChatName ? activeChatName : deleteTarget.chatName)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function ChatSidebar({
  chatList,
  companyName,
  activeChatId,
  activeChatName,
  onSelectChat,
  onNewChat,
  collapsed,
  onCollapsedChange,
  isMobile,
  mobileOpen,
  onMobileClose,
  onDeleteChat,
}) {
  const {
    chats, groups, loading, error, retry, hasOlder, loadingOlder, loadOlder, patchChatName, forgetChat,
  } = chatList;

  const content = (
    <SidebarContent
      companyName={companyName}
      chats={chats}
      groups={groups}
      loading={loading}
      error={error}
      onRetry={retry}
      hasOlder={hasOlder}
      loadingOlder={loadingOlder}
      onLoadOlder={loadOlder}
      onRenamed={patchChatName}
      onDeleted={forgetChat}
      activeChatId={activeChatId}
      activeChatName={activeChatName}
      onSelectChat={onSelectChat}
      onNewChat={onNewChat}
      onDeleteChat={onDeleteChat}
    />
  );

  // < md: a temporary Drawer opened from the ChatTopBar menu icon (§3.3).
  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{ '& .MuiDrawer-paper': { width: 280, boxSizing: 'border-box' } }}
      >
        {content}
      </Drawer>
    );
  }

  // ≥ md: the shared resizable rail. Collapse stays controlled here (rather
  // than the shell's own state) because the page persists it and ChatTopBar
  // shows an expand affordance while it is collapsed.
  return (
    <LeftSidebar
      collapsed={collapsed}
      onCollapsedChange={onCollapsedChange}
      collapsedActions={(
        <Tooltip title="New chat" placement="right">
          <IconButton size="small" onClick={onNewChat}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    >
      {content}
    </LeftSidebar>
  );
}

export default ChatSidebar;
